import React, { FunctionComponent, useRef, useState } from 'react'
import { Field, Form } from 'react-final-form'
import { defineMessage, useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { isRequired } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/validationUtils'
import { NavBarProps } from '@lyrahealth-inc/shared-app-logic/src/features/header/types'
import { formatMessageHelper } from '@lyrahealth-inc/shared-app-logic/src/features/intl/utils/formatMessageHelper'
import {
  CustomerSubpopulationOption,
  CustomerSubpopulations,
} from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { RadioButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { RadioGroupRFF } from '../../atoms/formElements/RadioButtons'
import { XIcon } from '../../atoms/icons/XIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { TitleSize } from '../../styles/typeStyles'
import { Flex1View } from '../../templates/content/CommonViews'
import { FixedButtonFormPageTemplate } from '../../templates/fixedButtonFormPageTemplate/FixedButtonFormPageTemplate'
import { ThemeType } from '../../utils/themes/ThemeProvider'

export interface ChooseSubpopulationFormProps {
  subpopulationData: CustomerSubpopulations
  onSubmit?: (value: number) => Promise<void>
  onBackPress?: () => void
  showCloseIcon?: boolean
  customTitle?: string
  customButtonText?: string
  centerAlignTitle?: boolean
  centerAlignButton?: boolean
  isButtonInline?: boolean
  backgroundColor?: string
  navBarProps?: NavBarProps
}

const HeaderAndCloseButtonContainer = styled.View<{ theme: ThemeType; centerAlign?: boolean; customTitle?: boolean }>(
  ({ theme, centerAlign, customTitle }) => ({
    flexDirection: 'row',
    justifyContent: centerAlign ? 'center' : 'space-between',
    gap: theme.spacing['24px'],
    marginBottom: customTitle ? theme.spacing['16px'] : theme.spacing['24px'],
    ...(IS_WEB && !customTitle && { marginRight: theme.breakpoints.isMinWidthTablet ? '0' : theme.spacing['24px'] }),
  }),
)

const CustomTitle = styled(Title)(({ theme }) => ({
  ...(theme.breakpoints.isMinWidthTablet && { fontSize: 34, lineHeight: 40 }),
  fontWeight: 700,
}))

const Subheader = styled(Title)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['8px'],
  marginTop: theme.breakpoints.isMinWidthTablet ? theme.spacing['8px'] : '0',
}))

const ContentContainer = styled(Flex1View)<{ theme: ThemeType }>(({ theme }) => ({
  ...(!IS_WEB && { marginHorizontal: theme.spacing['16px'] }),
}))

const defaultSubheaderCopy = defineMessage({
  defaultMessage: 'Select the scenario that applies to you',
  description: 'Subpopulation redirection subheader informing user to pick group they belong to',
})

export const ChooseSubpopulationForm: FunctionComponent<ChooseSubpopulationFormProps> = ({
  subpopulationData,
  onSubmit,
  onBackPress,
  showCloseIcon = true,
  customTitle,
  customButtonText,
  backgroundColor,
  centerAlignTitle = false,
  centerAlignButton = false,
  isButtonInline = false,
  navBarProps,
}) => {
  const submit = useRef(noop)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { formatMessage } = useIntl()
  const { breakpoints, colors } = useTheme() as ThemeType
  const { header, subHeader, options } = subpopulationData

  const displayTitle = customTitle || formatMessageHelper(header)
  const displayButtonText =
    customButtonText ||
    formatMessage({
      defaultMessage: 'Continue',
      description: 'Button to confirm subpopulation selection',
    })

  const optionsWithValue = options.map((item, index) => {
    // Set value as index so we can retrieve the selected option
    const newItem: CustomerSubpopulationOption & { value: number } = { ...item, value: index }
    newItem.label = formatMessageHelper(newItem.label)
    newItem.description = formatMessageHelper(newItem.description)
    return newItem
  })

  const onFormSubmit = async (values: { subpopulation: number }) => {
    const selectedOptionIndex = values.subpopulation
    if (onSubmit) {
      setIsSubmitting(true)
      try {
        await onSubmit(selectedOptionIndex)
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const navBarPropsWithBack = {
    ...navBarProps,
  }
  if (onBackPress) {
    navBarPropsWithBack.onBack = onBackPress
  }

  return (
    <FixedButtonFormPageTemplate
      onSubmitPress={() => submit.current()}
      testID={'ChooseSubpopulationForm'}
      buttonText={displayButtonText}
      isButtonTransparent
      hideHeaderNav={IS_WEB}
      navBarProps={navBarPropsWithBack}
      loading={isSubmitting}
      inlineButton={isButtonInline}
      stickyViewBackgroundColor={backgroundColor}
      customStyle={breakpoints.isMobileSized ? { marginHorizontal: 8 } : { marginTop: 0 }}
      buttonContainerCustomStyle={{
        ...(IS_WEB && breakpoints.isMobileSized ? { paddingHorizontal: 24 } : { marginVertical: 0 }),
        ...(centerAlignButton && {
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }),
      }}
    >
      <ContentContainer>
        <HeaderAndCloseButtonContainer centerAlign={centerAlignTitle} customTitle={!!customTitle}>
          {customTitle ? (
            <CustomTitle text={displayTitle} size={TitleSize.LARGE} />
          ) : (
            <Title text={displayTitle} size={TitleSize.LARGE} />
          )}
          {IS_WEB && breakpoints.isMinWidthTablet && showCloseIcon && (
            <PressableOpacity
              onPress={onBackPress}
              accessibilityLabel={formatMessage({
                defaultMessage: 'Close',
                description: 'Accessibility label close button',
              })}
            >
              <XIcon size={20} fillColor={colors.iconDefault} />
            </PressableOpacity>
          )}
        </HeaderAndCloseButtonContainer>
        {!customTitle && (
          <Subheader text={formatMessageHelper(subHeader || defaultSubheaderCopy)} size={TitleSize.XSMALL} level='3' />
        )}
        <Form
          onSubmit={onFormSubmit}
          render={({ handleSubmit }) => {
            submit.current = handleSubmit
            return (
              <Field
                name='subpopulation'
                component={RadioGroupRFF}
                buttonType={RadioButtonType.CIRCLE_DESCRIPTION_OUTLINE}
                buttons={optionsWithValue}
                validate={(value) => isRequired(value, formatMessage)}
                baseInputStyle={breakpoints.isMinWidthTablet ? { marginBottom: 24 } : undefined}
              />
            )
          }}
        />
      </ContentContainer>
    </FixedButtonFormPageTemplate>
  )
}
